<template>
	<div>
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<v-row>
					<v-col cols="12">
						<div class="form-base__title">
							{{ label }}
						</div>
					</v-col>

					<v-col cols="3">
						<v-text-field
							v-model="v.lastName.$model"
							:errorMessages="getValidationErrorText(v.lastName)"
							label="Фамилия"
							outlined
						/>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="v.firstName.$model"
							:errorMessages="getValidationErrorText(v.firstName)"
							label="Имя"
							outlined
						/>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="v.patronymic.$model"
							:errorMessages="getValidationErrorText(v.patronymic)"
							label="Отчество (при наличии)"
							outlined
						/>
					</v-col>
					<v-col cols="3">
						<v-text-field
							v-model="v.trusteeStatus.$model"
							:errorMessages="getValidationErrorText(v.trusteeStatus)"
							label="Статус представителя (при наличии)"
							outlined
						/>
					</v-col>

					<v-col cols="6">
						<v-text-field
							v-model="v.snils.$model"
							:errorMessages="getValidationErrorText(v.snils)"
							label="СНИЛС"
							v-mask="['###-###-### ##']"
							outlined
							disabled
						/>
					</v-col>
					<v-col cols="6">
						<v-text-field
							v-model="v.inn.$model"
							:errorMessages="getValidationErrorText(v.inn)"
							label="ИНН"
							v-mask="['############']"
							outlined
							disabled
						/>
					</v-col>
				</v-row>

				<v-col cols="12">
					<div class="form-base__title">
						Контактные данные {{ contactsRoleLabel }}
					</div>
				</v-col>

				<v-row>
					<v-col cols="6">
						<v-text-field
							v-model="v.email.$model"
							:errorMessages="getValidationErrorText(v.email)"
							:label="'Адрес электронной почты ' + contactsRoleLabel"
							outlined
						/>
					</v-col>
					<v-col cols="6">
						<v-text-field
							v-model="v.phone.$model"
							:errorMessages="getValidationErrorText(v.phone)"
							v-mask="Mask.PHONE"
							:label="'Контактный телефон ' + contactsRoleLabel"
							outlined
						/>
					</v-col>
				</v-row>

				<StatusDocument :v="v.documents.statusDocument"/>

				<MrpaBaseAuthority :v="v.baseAuthority"/>
			</div>
		</div>

		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<NotaryInfo :v="v"/>
			</div>
		</div>
		<div v-if="v.assistantJob" class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<Assistant :v="v"/>
			</div>
		</div>
	</div>
</template>

<script>
import {
	maxLength,
	minLength,
	required,
	requiredIf,
} from 'vuelidate/lib/validators';
import {
	EMAIL,
	EMOJI,
	getValidationErrorText,
	MAX_LENGTH,
	MAX_LENGTH_EMAIL,
	MinLength,
	PERSON_NAME,
	TRAILING_AND_LEADING_SPACES,
} from '@/utils/validate';
import StatusDocument, {
	statusDocumentModel,
	getStatusDocumentValidation,
} from '@/components/services/elements/StatusDocument';
import cloneDeep from 'clone-deep';
import MrpaBaseAuthority, {
	mrpaBaseAuthorityModel,
	mrpaBaseAuthorityValidation,
} from '@/components/services/elements/MrpaBaseAuthority';
import NotaryInfo, {
	notaryInfoModel,
	notaryInfoValidation,
} from '@/components/services/elements/NotaryInfo';
import Assistant, {
	assistantValidation,
	assistantModel,
} from '@/components/services/elements/Assistant';
import {DocumentType} from '@/types/services';
import {Mask, MrpaType} from '@/constants';
import deepmerge from 'deepmerge';
import {Role} from '@/utils/dictionaries';

export const getMrpaTrusteeValidation = (trustee) => {
	let validation = {
		lastName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES,
		},
		firstName: {
			required,
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES,
		},
		patronymic: {
			maxLength: maxLength(MAX_LENGTH),
			PERSON_NAME,
			TRAILING_AND_LEADING_SPACES,
		},
		trusteeStatus: {
			maxLength: maxLength(1000),
			EMOJI,
			TRAILING_AND_LEADING_SPACES,
			required: requiredIf(
				() =>
					trustee.documents.statusDocument.name ||
					trustee.documents.statusDocument.docDate ||
					trustee.documents.statusDocument.issuer ||
					trustee.documents.statusDocument.series ||
					trustee.documents.statusDocument.number ||
					trustee.documents.statusDocument.file ||
					trustee.documents.statusDocument.fileSig
			),
		},
		snils: {},
		inn: {},
		documents: {
			statusDocument: getStatusDocumentValidation(trustee),
		},
		baseAuthority: mrpaBaseAuthorityValidation,
		...notaryInfoValidation,

		email: {
			required,
			EMAIL,
			maxLength: maxLength(MAX_LENGTH_EMAIL),
			TRAILING_AND_LEADING_SPACES,
		},
		phone: {
			required,
			minLength: minLength(MinLength.PHONE),
		},
	};

	if (trustee.assistantJob) {
		validation = deepmerge(validation, assistantValidation);
	}

	return validation;
};

export const mrpaTrusteeModel = {
	lastName: '',
	firstName: '',
	patronymic: '',
	trusteeStatus: '',
	snils: '',
	inn: '',
	documents: {
		statusDocument: cloneDeep(statusDocumentModel),
		authorityDocument: {
			// Для МЧД по файлу. Сохранять ссылки на файлы МЧД
			type: DocumentType.MRP,
			file: null,
			fileSig: null,
		},
	},
	baseAuthority: cloneDeep(mrpaBaseAuthorityModel),
	...notaryInfoModel,

	email: '',
	phone: '',
};

export const getDefaultMachineTrustee = (trustee) => {
	let trusteeVal = cloneDeep(trustee);

	if (trusteeVal.assistantJob) {
		trusteeVal = deepmerge(assistantModel, trusteeVal);
	}

	if (!trusteeVal.documents) return trusteeVal;

	return deepmerge(trusteeVal, {
		documents: {
			statusDocument:
				trusteeVal.documents.statusDocument ?? statusDocumentModel,
			authorityDocument:
				trusteeVal.documents.authorityDocument ??
				mrpaTrusteeModel.documents.authorityDocument,
		},
	});
};

export const assemblyMrpaTrustee = ({trustee, mrpa}) => {
	return deepmerge(trustee, {
		baseAuthority: {
			mrpIssuerInn: mrpa.type === MrpaType.BY_GUID ? mrpa.issuerInn : null,
		},
		documents: {
			statusDocument: trustee.documents.statusDocument.name
				? trustee.documents.statusDocument
				: null,
			authorityDocument:
				mrpa.type === MrpaType.BY_GUID
					? null
					: trustee.documents.authorityDocument,
		},
	});
};

export default {
	name: 'MrpaTrustee',
	components: {
		StatusDocument,
		MrpaBaseAuthority,
		NotaryInfo,
		Assistant,
	},
	props: {
		v: {
			type: Object,
			required: true,
		},
		label: {
			type: String,
			required: true,
		},
		roleType: {
			type: String,
			required: false,
		},
	},
	data: () => ({
		Mask,
		getValidationErrorText,
	}),
	computed: {
		contactsRoleLabel() {
			if (this.roleType !== null) {
				if (this.roleType === Role.ORGANIZATION) {
					return 'представителя юридического лица';
				} else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
					return 'представителя иностранного юридического лица';
				}
			}
			return '';
		},
	},
};
</script>
